/* @jsx h */
import { api, instrumentation } from "@app/data/index";
import {
  CheckoutData,
  CheckoutDiscount,
  CheckoutDiscountComponent,
  CheckoutResult,
  isPaymentErrorCode,
  PaymentDetails,
  Promotion,
  StaffCheckoutStagedPayment,
} from "@lemmonhq/types";
import {
  calculateCheckoutChargeAmount,
  calculateCheckoutSubTotal,
  calculateCheckoutTotal,
  isPaidByTableCredit,
  calculateLegScopedPromotionsTotal,
  calculateLegScopedRewards,
  formatPrice,
  isItemScopedPromotion,
  round,
  staffCheckoutLegIdempotencyKey,
} from "@lemmonhq/orders";
import { Loading } from "@app/vue/components/index";
import { useCheckout } from "@app/vue/providers/Checkout";
import { useAuth, useShop, useToast } from "@app/vue/providers/index";
import {
  computed,
  defineComponent,
  ref,
  h,
  onMounted,
  onUnmounted,
  watch,
} from "vue";
import * as Sentry from "@sentry/vue";
import type { IntegratedPaymentWorkflowStatus } from "../../../workflows/types";

import { CardModal } from "./CardModal";
import { CashModal } from "./CashModal";
import { LeavePartPaidConfirmModal } from "./LeavePartPaidConfirmModal";
import { CheckoutEditor } from "./CheckoutEditor";
import { DiscountModal } from "./DiscountModal";
import {
  buildCheckoutDiscount,
  getDiscountComponents,
} from "./DiscountBreakdownModal";
import { ProductDiscountEditorSheet } from "./ProductDiscountEditorSheet";
import { TerminalPaymentModal } from "./TerminalPaymentModal";
import { WorkflowPaymentStatusModal } from "./WorkflowPaymentStatusModal";
import {
  clearPaymentTraceContext,
  endPaymentTrace,
  startPaymentSpan,
} from "./paymentTrace";
import {
  POLL_INTERVAL_MS_BY_WORKFLOW_PLATFORM,
  findCheckoutIntegration,
} from "./paymentIntegrationConfigs";
import { membershipRefusalMessage } from "./membershipRefusal";
import { TransactionSuccess } from "./TransactionSuccess";
import { TransactionSummary } from "./TransactionSummary";
import { CheckoutActions, getPrintJobForTableBill } from "./CheckoutActions";
import { CheckoutActionsV2 } from "./CheckoutActionsV2";
import { ManualPaymentModal } from "./ManualPaymentModal";
import {
  FullScreenSheet,
  useLocalization,
  type FullScreenSheetSlotProps,
} from "@lemmonhq/uikit";
import { OnlinePaymentModalV3 } from "./OnlinePaymentModalV3";
import { PromotionsModal } from "./PromotionsModal";
import { LoyaltyModal } from "./LoyaltyModal";
import type { ResolvedCheckoutCustomer } from "./LoyaltyModal";
import {
  calculateApplicablePromotions,
  evaluateLoyaltySelection,
  isLoyaltyPromotion,
} from "@lemmonhq/orders";
import { pp } from "@app/utils/clientBusiness";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useNativeBridge } from "@app/vue/providers/NativeBridge";

export type CheckoutComponentProps = {
  onSuccess: Function;
  onCancel: Function;
  checkoutId: string;
};
// How long a single status poll may stay in flight. Well above the p99 handler
// time (tens of ms) but far below the point where a stuck request would starve
// the poll loop.
const POLL_REQUEST_TIMEOUT_MS = 10000;

const isAbortError = (error: unknown): boolean =>
  (error instanceof DOMException && error.name === "AbortError") ||
  (error as { name?: string })?.name === "AbortError" ||
  (error as { cause?: { name?: string } })?.cause?.name === "AbortError";

export const CheckoutComponent = defineComponent<CheckoutComponentProps>({
  name: "CheckoutComponent",
  props: ["onSuccess", "onCancel", "checkoutId"],
  setup(props) {
    const checkout = useCheckout();
    // localstate
    const showCheckoutEditor = ref(false);
    const showDiscountModal = ref(false);
    const showProductDiscountEditorSheet = ref(false);
    const showPromotionsModal = ref(false);
    const showCashModal = ref(false);
    const showCardModal = ref(false);
    const showManualModal = ref(false);
    const showOnlineModal = ref(false);
    const showLoyaltyModal = ref(false);
    const showTerminalModal = ref<"card" | "twint" | false>(false);
    const isPrinting = ref(false);
    const toast = useToast();
    const shop = useShop();
    const auth = useAuth();
    const { t } = useLocalization();
    const trpc = useTrpc();
    const bridge = useNativeBridge();

    const paymentIntegrationPollingInterval = ref<ReturnType<
      typeof setInterval
    > | null>(null);
    const paymentIntegrationPollingInProgress = ref(false);
    // Sequence number for the current payment's polls. Sent to the server and
    // stamped on the poll span so the two can be joined by attribute rather
    // than by span parenting -- see the comment on `startPaymentSpan`.
    const paymentIntegrationPollIndex = ref(0);

    // Poll as fast as the platform actually advances the payment row. The
    // platform is unknown only for a checkout created before the server
    // started returning it, in which case the server's own default platform
    // (restate) is the right assumption.
    const paymentIntegrationPollIntervalMs = computed(() => {
      const platform =
        checkout.checkout.value?.paymentIntegrationWorkflowPlatform;
      return (
        (platform && POLL_INTERVAL_MS_BY_WORKFLOW_PLATFORM[platform]) ??
        POLL_INTERVAL_MS_BY_WORKFLOW_PLATFORM.restate
      );
    });
    //
    // {"nakka-terminal-v2": true, "payrexx-online-v2": true}
    const featureFlags = auth.user.value?.restaurant.feature_flags || {};

    const usePaymentIntegrationV2 = computed(() => {
      const user = auth.user.value;
      if (!user?.id) {
        return false;
      }

      // Terminal payments (a paired Payrexx terminal, or a Nakka terminal
      // unless nakka_browser is on) always run on the payment workflow.
      const integrationType = findCheckoutIntegration(user, trpc, bridge)
        ?.integrationType;
      if (integrationType === "payrexx_terminal" || integrationType === "nakka") {
        return true;
      }

      // Everything else (SoftPOS, Wallee, ...) needs payment_integration_v2 and
      // an integration listing this cashier.
      return (
        !!featureFlags["payment_integration_v2"] &&
        (user.payment_integrations || []).some((integration) =>
          integration.user_ids.includes(user.id),
        )
      );
    });

    console.log(checkout);

    const txSuccess = computed(
      () => checkout.checkout.value?.txSuccess || false,
    );
    const loading = computed(() => !!checkout.checkout.value?.txLoading);

    // What this checkout ACTION charges: while a split/partial leg is armed the
    // pay modals collect that leg's amount (net of any adjustment applied to the
    // leg), otherwise the whole-cart total.
    //
    // calculateCheckoutChargeAmount is the single definition of that figure —
    // the same one the provider adapters (terminal / SoftPOS) charge with and
    // the summary displays — so the modals, the screen and the terminal can
    // never disagree.
    const total = computed(() => {
      const cd = checkout.checkout.value;
      if (!cd) return 0;
      return calculateCheckoutChargeAmount(cd);
    });

    // Base the checkout-level discount is computed against: a single staged leg
    // while one is armed, else the whole cart subtotal — NET of any promotion
    // already reducing these items. Discounting the promotion-adjusted amount
    // (not the gross price) stops a % discount double-dipping with the promo, so
    // a 100% discount leaves 0 rather than a negative total.
    const adjustmentBase = computed(() => {
      const cd = checkout.checkout.value;
      if (!cd) return 0;
      const gross = cd.staffCheckout?.intent
        ? cd.staffCheckout.intent.amount
        : calculateCheckoutSubTotal(cd);
      return Math.max(0, round(gross - calculateLegScopedPromotionsTotal(cd)));
    });

    // Staged split/partial state helpers.
    const stagedIntent = computed(
      () => checkout.checkout.value?.staffCheckout?.intent,
    );
    const hasStagedPayments = computed(
      () => (checkout.checkout.value?.staffCheckout?.payments?.length ?? 0) > 0,
    );
    const isStagedMode = computed(
      () => !!stagedIntent.value || hasStagedPayments.value,
    );

    // Fully collected but no order yet: every leg is in, the balance is zero,
    // and finalize either failed or never ran (crash, lost response, resumed
    // from Transaction History). Without an explicit action for this state the
    // checkout is a dead end — no leg can be armed at zero remaining, the pay
    // buttons refuse, and closing is blocked because money is collected. So the
    // pay actions are replaced by a "Finish order" retry while it holds.
    const awaitingFinalize = computed(() => {
      const cd = checkout.checkout.value;
      if (!cd?.staffCheckout?.id || txSuccess.value || loading.value)
        return false;
      const legs = cd.staffCheckout.payments || [];
      if (legs.length === 0) return false;
      const collected = legs.reduce((s, p) => s + p.subtotal, 0);
      return round(calculateCheckoutSubTotal(cd) - collected) <= 0.01;
    });

    // Why the order wasn't created, when we know. Shown verbatim in the panel
    // below: "outside shift hours" or "tax rate required for product X" tells
    // the cashier whether tapping again will help or whether they need a
    // manager, which a generic "something went wrong" does not.
    const finalizeError = ref<string | null>(null);

    // The collected legs, for the panel's receipt-style list. Seeing each
    // payment itemised is what makes "the money is safe" believable — the
    // cashier can match it against the cash drawer and the card slips.
    const collectedLegs = computed(
      () => checkout.checkout.value?.staffCheckout?.payments ?? [],
    );
    const collectedTotal = computed(() =>
      round(collectedLegs.value.reduce((s, p) => s + p.amount, 0)),
    );
    const money = (n: number) =>
      formatPrice(n, true, 2, auth.user.value?.restaurant.currency || "CHF");

    // A collected split/partial leg that did NOT finalize the order — the
    // success screen shows a per-leg confirmation and the checkout stays open.
    const isLegSuccess = computed(() => {
      const s = checkout.checkout.value?.txSuccess;
      return !!s && s.type === "staff_checkout_payment_collected";
    });

    // Mid-split: at least one leg collected and the order not yet completed.
    // Leaving now asks for confirmation (see canClose). The final success
    // screen (non-leg txSuccess) clears it.
    const isPartPaid = computed(() => {
      const s = checkout.checkout.value?.txSuccess;
      if (s && s.type !== "staff_checkout_payment_collected") return false;
      return collectedLegs.value.length > 0;
    });

    // Resolver of the pending "leave a part-paid checkout?" prompt; non-null
    // while the confirm modal is shown.
    const leaveConfirmResolve = ref<((leave: boolean) => void) | null>(null);
    const confirmLeavePartPaid = () =>
      new Promise<boolean>((resolve) => {
        leaveConfirmResolve.value?.(false); // supersede any stale prompt
        leaveConfirmResolve.value = resolve;
      });
    const answerLeaveConfirm = (leave: boolean) => {
      const resolve = leaveConfirmResolve.value;
      leaveConfirmResolve.value = null;
      resolve?.(leave);
    };

    // The split/partial editor is a RightSheet opened from a header button
    // (all screen sizes) or via "Continue" after a leg. Available for a cart
    // checkout with no adjustments once its staff_checkout row exists.
    //
    // No environment gate: the rest of the feature (the staff_checkout row on
    // every cart checkout, the Open checkouts recovery list, resume) has always
    // run everywhere, so gating only the button left the risky half exposed and
    // the safe half hidden. The real gate is `usePaymentIntegrationV2` below —
    // a per-merchant, per-user feature flag.
    //
    // Whether this checkout is a cart at all. The split button is the sole
    // editor entry for cart checkouts and is rendered throughout — independent
    // of readiness/loading/adjustments — so it never flickers with the generic
    // edit button while the staff_checkout row is being created; it just
    // disables until the checkout is actually splittable.
    const showSplitButton = computed(() => {
      const cd = checkout.checkout.value;
      if (!cd) return false;
      // Split/partial requires the V2 integrated-payment path for card/terminal
      // legs. Non-V2 merchants only have the legacy terminal modal, which is
      // blocked in staged mode (and would capture money before that block), so
      // never let them enter staged mode in the first place.
      if (!usePaymentIntegrationV2.value) return false;
      if (cd.payload?.type !== "cart") return false;
      if (txSuccess.value) return false;
      return true;
    });
    // Ready to actually open the sheet (row created, not mid-collection).
    const canSplitPartial = computed(() => {
      const cd = checkout.checkout.value;
      if (!showSplitButton.value || !cd) return false;
      if (!cd.staffCheckout?.id || loading.value) return false;
      // Already in a staged flow (a leg is armed, or legs are collected): always
      // allow reopening the editor to re-pick the leg. Any per-leg adjustment is
      // reset on reopen (see the split button's onClick), so it can't go stale.
      const staged =
        !!cd.staffCheckout.intent ||
        (cd.staffCheckout.payments?.length ?? 0) > 0;
      if (staged) return true;
      // Not yet split: a whole-cart adjustment is an order-level intent, which
      // routes to the single-payment path rather than a per-leg split.
      const hasDiscount = !!cd.discount?.amount && cd.discount.amount > 0;
      // Item-scoped promotions (happy-hour `special_price`, `product_discount`)
      // split cleanly along item lines and are preserved into the split flow
      // (see `openSplitEditor`); only genuinely order-level promotions
      // (`order_discount`) still force the single-payment path.
      // See docs/plans/checkout_editor/11_promotion_aware_split.md.
      const hasBlockingPromotions = (cd.promotions ?? []).some(
        (p) => !isItemScopedPromotion(p),
      );
      const hasGiftCards = (cd.giftCards?.length ?? 0) > 0;
      // A delivery fee is not part of the split target (which counts item
      // subtotal only), so splitting a delivery checkout would collect less
      // than the order total and materialize an underpaid order. Keep it on the
      // single-payment path until per-leg fee distribution lands (subtask 10).
      const hasDeliveryFee =
        cd.payload?.type === "cart" &&
        !!cd.payload.object.deliveryFee?.value &&
        cd.payload.object.deliveryFee.value > 0;
      return (
        !hasDiscount &&
        !hasBlockingPromotions &&
        !hasGiftCards &&
        !hasDeliveryFee
      );
    });

    // Reopen the split editor to re-pick the current leg. Clears the armed
    // intent and any order-level adjustment (discount/gift card) applied to it,
    // so the cashier starts the leg selection fresh — collected legs are
    // untouched. Item-scoped promotions (happy hour) are order-wide facts, not
    // per-leg picks, so they are PRESERVED and re-scoped to each leg by
    // `calculateLegScopedRewards`. See
    // docs/plans/checkout_editor/11_promotion_aware_split.md.
    const openSplitEditor = () => {
      const cd = checkout.checkout.value;
      const keptPromotions = (cd?.promotions ?? []).filter(
        isItemScopedPromotion,
      );
      if (
        cd &&
        (cd.staffCheckout?.intent ||
          cd.discount ||
          (cd.promotions?.length ?? 0) !== keptPromotions.length ||
          cd.giftCards?.length)
      ) {
        checkout.setCheckout({
          ...cd,
          staffCheckout: cd.staffCheckout
            ? { ...cd.staffCheckout, intent: undefined }
            : undefined,
          discount: undefined,
          promotions: keptPromotions,
          giftCards: [],
        });
      }
      showCheckoutEditor.value = true;
    };

    const canUseProductDiscountEditor = computed(() => {
      const payload = checkout.checkout.value?.payload;
      return (
        payload?.type === "cart" ||
        (payload?.type === "table" &&
          (payload.object.type === "full" || payload.object.type === "partial"))
      );
    });

    onMounted(async () => {
      if (!checkout.checkout.value) {
        console.log("no checkout");
        return;
      }
      // Multi-legged payment already in progress (e.g. resumed mid-split):
      // open the split/partial sheet so the cashier collects the next leg.
      if ((checkout.checkout.value.staffCheckout?.payments?.length ?? 0) > 0) {
        showCheckoutEditor.value = true;
      }
      const promotions = shop.getPromotions();
      console.log("services", pp(shop.services.value));
      const appliedPromotions = calculateApplicablePromotions(
        shop.services.value,
        checkout.checkout.value as CheckoutData,
        promotions as Promotion[],
      );
      console.log("appliedPromotions", appliedPromotions);
      const loyaltyIds = new Set(
        (promotions as Promotion[]).filter(isLoyaltyPromotion).map((p) => p.id),
      );
      const retained = (checkout.checkout.value.promotions ?? []).filter(
        (a) =>
          !loyaltyIds.has(a.promotion_id) &&
          !appliedPromotions.some((b) => b.promotion_id === a.promotion_id),
      );
      checkout.checkout.value.promotions = [...retained, ...appliedPromotions];
      if (checkout.checkout.value.customer?.reward_selections !== undefined)
        handleCustomerResolved(
          checkout.checkout.value.customer as ResolvedCheckoutCustomer,
        );
      const sum = appliedPromotions.reduce((sum, p) => sum + p.value, 0);
      console.log(pp(checkout.checkout.value));
      console.log("appliedPromotions", appliedPromotions, sum);

      // Open a staff_checkout lifecycle record for cart (non-table) checkouts.
      // This backs split/partial payments and crash recovery; the id is threaded
      // through the regular single-payment path too (create_staff_order closes
      // it). Skip if one already exists (a resumed checkout carries its id).
      // Non-blocking: a failure here must not stop the cashier paying — the
      // split/partial buttons guard on the staff_checkout id being present.
      const cd = checkout.checkout.value;
      if (cd?.payload?.type === "cart" && !cd.staffCheckout?.id) {
        try {
          const cart = cd.payload.object;
          const { id } = await trpc.staffCheckouts.create.mutate({
            serviceMethod: (cd.serviceMethod ?? "takeaway") as
              | "takeaway"
              | "delivery"
              | "dine-in",
            cart,
            discount: cd.discount,
            tips: cd.tip?.value,
            tableId: cart.tableId ?? undefined,
          });
          if (checkout.checkout.value) {
            checkout.setCheckout({
              ...checkout.checkout.value,
              staffCheckout: {
                ...checkout.checkout.value.staffCheckout,
                id,
                payments: checkout.checkout.value.staffCheckout?.payments ?? [],
              },
            });
          }
        } catch (e) {
          console.error("staffCheckouts.create failed", e);
          Sentry.captureException(e);
        }
      }
    });

    const handleGiftCardApply = (code: string, amount: number) => {
      if (!checkout.checkout.value) return;
      const existing = checkout.checkout.value.giftCards || [];
      checkout.setCheckout({
        ...checkout.checkout.value,
        giftCards: [
          ...existing.filter((g) => g.code !== code),
          { code, amount },
        ],
      });
    };

    const handleCustomerResolved = (customer: ResolvedCheckoutCustomer) => {
      if (!checkout.checkout.value) return;
      const cd = { ...checkout.checkout.value, customer } as CheckoutData;
      const promotions = shop.getPromotions() as Promotion[];
      const loyaltyIds = new Set(
        promotions.filter(isLoyaltyPromotion).map((p) => p.id),
      );
      const ordinary = (cd.promotions ?? []).filter(
        (p) => !loyaltyIds.has(p.promotion_id),
      );
      const rewards = evaluateLoyaltySelection(
        shop.services.value,
        cd,
        promotions,
        customer.reward_selections ?? [],
        ordinary,
      );
      checkout.setCheckout({
        ...cd,
        promotions: [...ordinary, ...rewards.applications],
      });
    };

    // Re-evaluate reward values when the bill changes, retaining explicit opt-outs.
    watch(
      () =>
        JSON.stringify([
          checkout.checkout.value?.payload,
          checkout.checkout.value?.serviceMethod,
          checkout.checkout.value?.discount,
          checkout.checkout.value?.staffCheckout?.intent,
          checkout.checkout.value?.customer?.reward_selections,
        ]),
      () => {
        const cd = checkout.checkout.value;
        if (!cd?.customer?.reward_selections || cd.txLoading || cd.txSuccess)
          return;
        handleCustomerResolved(cd.customer as ResolvedCheckoutCustomer);
      },
    );

    const handleDiscountConfirm = (discount: CheckoutDiscount | undefined) => {
      showDiscountModal.value = false;
      if (!checkout.checkout.value) return;

      const existingComponents = getDiscountComponents(
        checkout.checkout.value.discount,
      );
      const editorManagedComponents = existingComponents.filter(
        (component) => component.source === "order-items",
      );
      const modalComponents = getDiscountComponents(discount);

      checkout.setCheckout({
        ...checkout.checkout.value,
        discount: buildCheckoutDiscount([
          ...editorManagedComponents,
          ...modalComponents,
        ]),
      });
    };

    const handleProductDiscountApply = (
      component: CheckoutDiscountComponent,
    ) => {
      showProductDiscountEditorSheet.value = false;
      if (!checkout.checkout.value) return;

      const components = [
        ...getDiscountComponents(checkout.checkout.value.discount),
        component,
      ];

      checkout.setCheckout({
        ...checkout.checkout.value,
        discount: buildCheckoutDiscount(components),
      });
    };

    const handleManualConfirm = async (
      paymentManualType: string,
      reference: string,
      tip?: number,
    ) => {
      showManualModal.value = false;
      showCashModal.value = false;
      // Staged split/partial leg — collect against the staff_checkout instead
      // of creating a whole-cart order.
      if (stagedIntent.value) {
        await collectStagedPayment("manual", tip, {
          type: paymentManualType,
          reference,
        });
        return;
      }
      if (hasStagedPayments.value) {
        toast.showToast(
          t("checkout.staged_pick_next_leg") ||
            "Open the split editor to collect the next payment",
          { type: "warning" },
        );
        return;
      }
      const amountReceived = total.value;
      const tipAmount = tip || 0;
      const checkoutData: CheckoutData = {
        ...checkout.checkout.value,
        payment: {
          amountRcvd: amountReceived,
          paymentType: "manual",
          paymentManualType: paymentManualType,
          paymentReference: reference,
        },
        ...(tipAmount ? { tip: { value: tipAmount } } : {}),
      };
      checkout.setCheckout(checkoutData);
      confirmPayment(checkoutData);
    };
    // Table credit covers everything left to collect: whichever method the
    // cashier taps, the payment is the credit alone, with no cash or terminal
    // step (docs/plans/table_credit/pos_checkout_credit.md).
    const handlePayFromCredit = () => {
      const checkoutData: CheckoutData = {
        ...checkout.checkout.value,
        payment: { amountRcvd: 0, paymentType: "table_credit" },
      };
      checkout.setCheckout(checkoutData);
      confirmPayment(checkoutData);
    };

    watch(
      [
        showCashModal,
        showCardModal,
        showManualModal,
        showTerminalModal,
        showOnlineModal,
      ],
      (open) => {
        const cd = checkout.checkout.value;
        if (!open.some(Boolean) || !cd || !isPaidByTableCredit(cd)) return;
        showCashModal.value = false;
        showCardModal.value = false;
        showManualModal.value = false;
        showTerminalModal.value = false;
        showOnlineModal.value = false;
        handlePayFromCredit();
      },
      { flush: "sync" },
    );

    const handleCashConfirm = (amountReceived: number, tipAmount?: number) => {
      showCashModal.value = false;
      if (stagedIntent.value) {
        collectStagedPayment("cash", tipAmount);
        return;
      }
      if (hasStagedPayments.value) {
        toast.showToast(
          t("checkout.staged_pick_next_leg") ||
            "Open the split editor to collect the next payment",
          { type: "warning" },
        );
        return;
      }
      const checkoutData: CheckoutData = {
        ...checkout.checkout.value,
        payment: { amountRcvd: amountReceived, paymentType: "cash" },
        ...(tipAmount ? { tip: { value: tipAmount } } : {}),
      };
      checkout.setCheckout(checkoutData);
      confirmPayment(checkoutData);
    };

    // Card/terminal/online legs for a staged checkout run through the
    // integrated-payment workflow (subtask 8). Until that lands, block them
    // while a split/partial checkout is in progress so a whole-cart order can
    // never be created on top of already-staged legs (double charge).
    const blockNonCashManualWhenStaged = (label: string): boolean => {
      if (!isStagedMode.value) return false;
      toast.showToast(
        t("checkout.staged_use_cash_manual") ||
          `${label} isn't available for split/partial yet — use cash or manual`,
        { type: "warning" },
      );
      return true;
    };

    const handleCardConfirm = (amountReceived: number, tipAmount?: number) => {
      showCardModal.value = false;
      if (blockNonCashManualWhenStaged("Card")) return;
      const checkoutData: CheckoutData = {
        ...checkout.checkout.value,
        payment: { amountRcvd: amountReceived, paymentType: "card" },
        ...(tipAmount ? { tip: { value: tipAmount } } : {}),
      };
      checkout.setCheckout(checkoutData);
      confirmPayment(checkoutData);
    };

    const handleTerminalConfirm = async (
      amountReceived: number,
      paymentId: number,
      tipAmount?: number,
    ) => {
      if (blockNonCashManualWhenStaged("Terminal")) {
        showTerminalModal.value = false;
        return;
      }
      try {
        if (checkout.checkout.value) {
          checkout.setCheckout({
            ...checkout.checkout.value,
            txLoading: true,
          });
        }
        const { result, payment } = await api.getPaymentTxResult(paymentId);
        console.debug("results", result);
        printCartCheckoutResult(result);
        handleTxSuccess(result);
        showTerminalModal.value = false;
      } catch (e) {
        console.error(e);
        alert("Payment confirmed yet failed to process order");
      } finally {
        if (checkout.checkout.value) {
          checkout.setCheckout({
            ...checkout.checkout.value,
            txLoading: false,
          });
        }
      }
    };
    const handleOnlineConfirm = async (
      amountReceived: number,
      paymentId: number,
      tipAmount?: number,
    ) => {
      if (blockNonCashManualWhenStaged("Online")) {
        showOnlineModal.value = false;
        return;
      }
      try {
        if (checkout.checkout.value) {
          checkout.setCheckout({
            ...checkout.checkout.value,
            txLoading: true,
          });
        }
        const { result, payment } = await api.getPaymentTxResult(paymentId);
        console.debug("results", result);
        printCartCheckoutResult(result);
        handleTxSuccess(result);
        showOnlineModal.value = false;
      } catch (e) {
        console.error(e);
        alert("Payment confirmed yet failed to process order");
      } finally {
        if (checkout.checkout.value) {
          checkout.setCheckout({
            ...checkout.checkout.value,
            txLoading: false,
          });
        }
      }
    };
    let closeTimeout: NodeJS.Timeout;
    const clearCloseTimeout = () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
    };
    const handleTxSuccess = (o: CheckoutResult) => {
      if (checkout.checkout.value) {
        checkout.setCheckout({
          ...checkout.checkout.value,
          txSuccess: o,
          txLoading: false,
          // Clear workflow state on success
          paymentIntegrationWorkflowId: undefined,
          paymentIntegrationWorkflowPlatform: undefined,
          paymentIntegrationWorkflowStatus: undefined,
        });
      }
      closeTimeout = setTimeout(() => {
        props.onSuccess();
      }, 10000);
      //      try {
      //        const audio = new Audio(paySuccessSound);
      //        audio.play();
      //      } catch (e) {}
    };

    // Per-leg confirmation for a split/partial checkout that is NOT yet fully
    // paid. Shows the success checkmark for the collected leg but keeps the
    // checkout open (no auto-close) so the cashier collects the next leg.
    const handleLegCollected = (o: CheckoutResult) => {
      if (!checkout.checkout.value) return;
      checkout.setCheckout({
        ...checkout.checkout.value,
        txSuccess: o,
        txLoading: false,
        paymentIntegrationWorkflowId: undefined,
        paymentIntegrationWorkflowPlatform: undefined,
        paymentIntegrationWorkflowStatus: undefined,
      });
      // Deliberately no auto-close: the split flow continues.
    };

    // "Continue" from a per-leg confirmation: clear the success and open the
    // split/partial sheet so the cashier picks the next leg.
    const collectNextLeg = () => {
      if (!checkout.checkout.value) return;
      checkout.setCheckout({
        ...checkout.checkout.value,
        txSuccess: undefined,
      });
      showCheckoutEditor.value = true;
    };

    // Print a receipt for the just-collected leg (payment-scoped, since the
    // order isn't finalized until the last leg).
    const printLeg = async () => {
      const s = checkout.checkout.value?.txSuccess;
      if (s?.type !== "staff_checkout_payment_collected") return;
      try {
        const { result } = await api.getPaymentTxResult(s.paymentId);
        await printCartCheckoutResult(result);
      } catch (e) {
        console.error("printLeg failed", e);
        toast.showToast("Could not print leg receipt", { type: "error" });
      }
    };

    onUnmounted(() => {
      if (paymentIntegrationPollingInterval.value) {
        clearInterval(paymentIntegrationPollingInterval.value);
      }
      clearCloseTimeout();
      // Unmount is an exit path like any other: close out the payment's trace.
      clearPaymentTraceContext(props.checkoutId, "abandoned");
    });

    // Cancel the open staff_checkout when the cashier abandons the flow, so it
    // doesn't linger as recoverable. No-op once paid (txSuccess) or when the
    // checkout is not a staged cart checkout. Idempotent server-side.
    //
    // Returns `retained: true` when the server did NOT cancel because the
    // checkout already has collected (confirmed) legs — it is left `open` and
    // recoverable from Transaction History instead of being discarded. The
    // caller still lets the sheet close (leaving never strands money); it only
    // uses this to tell the cashier the checkout was saved rather than cancelled.
    const cancelStaffCheckout = async (): Promise<{ retained: boolean }> => {
      const id = checkout.checkout.value?.staffCheckout?.id;
      if (!id || txSuccess.value) return { retained: false };
      try {
        const res = await trpc.staffCheckouts.cancel.mutate({ id });
        return { retained: !!res.retained_open };
      } catch (e) {
        console.error("staffCheckouts.cancel failed", e);
        return { retained: false };
      }
    };

    const setCheckoutLoadingState = (txLoading: boolean) => {
      if (!checkout.checkout.value) return;
      checkout.setCheckout({
        ...checkout.checkout.value,
        txLoading,
      });
    };

    const printCartCheckoutResult = async (result: CheckoutResult) => {
      if (!result || result.type !== "cart") {
        console.error(result);
        return;
      }

      try {
        await api.processOrderCreation(shop, result);
      } catch (e) {
        console.error(e);
        alert("Payment confirmed & Order created yet failed to print tickets");
      }
    };

    const confirmPayment = async (checkout: CheckoutData) => {
      try {
        setCheckoutLoadingState(true);
        if (checkout.payload?.type === "cart") {
          const { order, printJobs, payment } = await api.createOrder(
            checkout,
            shop,
          );
          console.log("confirmPayment", { order, printJobs, payment });
          handleTxSuccess({
            type: "cart",
            order,
            printJobs,
            payment: payment as PaymentDetails,
          });
          return order;
        } else {
          const tableCheckout = checkout.payload?.object;
          if (tableCheckout?.type == "split") {
            const { table, taxReceipt, payment } =
              await api.createSplitlPaymentForTable(checkout);
            handleTxSuccess({ type: "table", table, taxReceipt, payment });
          } else if (tableCheckout?.type == "partial") {
            const { table, taxReceipt, payment } =
              await api.createPartialPaymentForTable(checkout);
            handleTxSuccess({ type: "table", table, taxReceipt, payment });
          } else {
            const { table, taxReceipt, payment } =
              await api.createPaymentForTable(checkout);
            handleTxSuccess({ type: "table", table, taxReceipt, payment });
          }
        }
      } catch (e: any) {
        console.error(e);
        toast.showToast(
          membershipRefusalMessage(e, t) ||
            e?.message ||
            "Failed processing payment",
          {
            type: "error",
          },
        );
        throw e;
      } finally {
        setCheckoutLoadingState(false);
      }
    };

    // Finalize the staff_checkout once every leg is collected (net balance ~0),
    // materializing the order. Shared by the cash/manual path here and the
    // terminal poll intercept (subtask 8) so the trigger can't drift.
    // Render the finalized staff-checkout order on the success screen (print +
    // handleTxSuccess). Takes an ALREADY-built { order, printJobs, payment } —
    // the order is created server-side now (inline in add_split/add_partial for
    // cash, or the apply_transition hook for card), so the client only renders it.
    const renderStaffCheckoutSuccess = async (built: {
      order: unknown;
      printJobs: unknown;
      payment?: unknown;
    }) => {
      const result: CheckoutResult = {
        type: "cart",
        order: built.order as any,
        printJobs: built.printJobs as any,
        payment: built.payment as PaymentDetails,
      };
      await printCartCheckoutResult(result);
      handleTxSuccess(result);
    };

    // Read back the finalized order for a fully-collected checkout and render it.
    // The order is created server-side (inline finalize); `finalize` is now
    // IDEMPOTENT — it returns the existing order under a row lock, never creating
    // a second one. Used as the read-back for the integrated/card path (whose
    // poll only carries the leg result), and as the fallback for cash when the
    // inline result is unavailable. Guarded on coverage so a stray call no-ops.
    const maybeFinalizeStaffCheckout = async () => {
      const cd = checkout.checkout.value;
      const staffCheckoutId = cd?.staffCheckout?.id;
      if (!staffCheckoutId) return;
      // Count GROSS coverage (each leg's subtotal, tip-exclusive) against the
      // cart subtotal, so a discounted leg still reaches zero remaining.
      const target = calculateCheckoutSubTotal(cd);
      const collected = (cd.staffCheckout?.payments || []).reduce(
        (s, p) => s + p.subtotal,
        0,
      );
      if (round(target - collected) > 0.01) return; // more legs to collect
      const built = await trpc.staffCheckouts.finalize.mutate({
        checkoutId: staffCheckoutId,
      });
      await renderStaffCheckoutSuccess(built);
    };

    // Explicit retry for a fully-collected checkout whose order was never
    // created (see `awaitingFinalize`). staff_checkouts_finalize is idempotent —
    // it takes a row lock and returns the existing order if one was already
    // made — so pressing this twice can never produce two orders.
    const finalizeNow = async () => {
      try {
        finalizeError.value = null;
        setCheckoutLoadingState(true);
        await maybeFinalizeStaffCheckout();
      } catch (e: any) {
        console.error("finalizeNow failed", e);
        Sentry.captureException(e);
        finalizeError.value = e?.message || null;
        toast.showToast(e?.message || "Could not create the order", {
          type: "error",
        });
      } finally {
        setCheckoutLoadingState(false);
      }
    };

    // Collect one staged split/partial leg (cash/manual) against the open
    // staff_checkout, then finalize if the balance is now zero.
    const collectStagedPayment = async (
      method: string,
      tipAmount?: number,
      manual?: { type: string; reference: string },
    ) => {
      const cd = checkout.checkout.value;
      const intent = cd?.staffCheckout?.intent;
      const staffCheckoutId = cd?.staffCheckout?.id;
      if (!staffCheckoutId || !intent) return;
      try {
        setCheckoutLoadingState(true);
        // Reflect the entered tip in checkout state so the "PAYING NOW" summary
        // shows net + tip during the collection round-trip, matching the success
        // screen (calculateCheckoutChargeAmount adds checkout.tip). Without this
        // the summary shows the pre-tip net and jumps on success — a flicker.
        // Reset on an intermediate leg below so it doesn't carry to the next leg.
        if (tipAmount != null) {
          checkout.setCheckout({ ...cd, tip: { value: tipAmount } });
        }
        // Legs already collected, as this client sees them. Doubles as the leg
        // SLOT: the key is derived from it (so a retry of THIS leg reuses its
        // key instead of minting a random new one), and the SQL rejects the
        // call outright if the DB disagrees — which is what actually stops a
        // retry-after-lost-response from collecting the same leg twice.
        const collectedLegCount = cd.staffCheckout?.payments?.length ?? 0;
        const idempotencyKeys = [
          staffCheckoutLegIdempotencyKey(staffCheckoutId, collectedLegCount),
        ];
        // Adjustments ride THIS leg only: the discount / promotions / gift
        // cards currently on the checkout are computed against the leg's gross
        // amount (see `adjustmentBase`), collected here, then reset so the next
        // leg starts clean. `subtotal` stays the gross share (drives the
        // remaining-balance / finalize math); `amount` is the net cash charged.
        const discountForLeg = cd.discount;
        // Promotions are engine-computed order-wide; scale them (total + each
        // application's value) to this leg's share so the payment record and the
        // captured promotion applications reflect the payer's portion. Same
        // helper the integrated (card) path uses server-side, so the two
        // transports can't diverge.
        const rewardsForLeg = calculateLegScopedRewards(cd) ?? undefined;
        const giftCardsForLeg = cd.giftCards?.length ? cd.giftCards : undefined;
        const legNet = round(
          intent.amount -
            (discountForLeg?.amount || 0) -
            (rewardsForLeg?.total || 0) -
            (giftCardsForLeg?.reduce((s, g) => s + g.amount, 0) || 0),
        );
        const legItems = intent.mode === "partial" ? intent.items : undefined;
        const res =
          intent.mode === "split"
            ? await trpc.staffCheckouts.addSplitPayment.mutate({
                checkoutId: staffCheckoutId,
                amount: intent.amount,
                paymentMethod: method,
                idempotencyKeys,
                expectedLegCount: collectedLegCount,
                tips: tipAmount,
                manualPayment: manual,
                discount: discountForLeg,
                rewards: rewardsForLeg,
                giftCardRedemptions: giftCardsForLeg,
                profileId: cd.customer?.profile_id,
                customerId: cd.customer?.id,
              })
            : await trpc.staffCheckouts.addPartialPayment.mutate({
                checkoutId: staffCheckoutId,
                totals: { subtotal: intent.amount },
                partialItems: intent.items,
                paymentMethod: method,
                idempotencyKeys,
                expectedLegCount: collectedLegCount,
                tips: tipAmount,
                manualPayment: manual,
                discount: discountForLeg,
                rewards: rewardsForLeg,
                giftCardRedemptions: giftCardsForLeg,
                profileId: cd.customer?.profile_id,
                customerId: cd.customer?.id,
              });

        const legs: StaffCheckoutStagedPayment[] = [
          ...(cd.staffCheckout?.payments || []),
          {
            paymentId: res.payment_id,
            paymentGuid: res.payment_guid,
            amount: legNet,
            subtotal: intent.amount,
            method,
            items: legItems,
          },
        ];
        const target = calculateCheckoutSubTotal(cd);
        const collectedNow = legs.reduce((s, p) => s + p.subtotal, 0);
        const isLastLeg = round(target - collectedNow) <= 0.01;

        // Record the leg. On an INTERMEDIATE leg, clear the armed intent and the
        // order-level per-leg adjustments so the next leg is computed fresh. On
        // the FINAL leg, KEEP them so the summary stays on "PAYING NOW <leg>"
        // through the finalize round-trip instead of flashing the whole-cart
        // TOTAL — the success screen replaces it immediately after finalize
        // resolves. Item-scoped promotions (happy hour) are order-wide and must
        // persist across every leg — each leg self-scopes its share via
        // `calculateLegScopedRewards` — so they are preserved, not cleared.
        const keptPromotions = (
          checkout.checkout.value!.promotions ?? []
        ).filter(isItemScopedPromotion);
        checkout.setCheckout({
          ...checkout.checkout.value!,
          staffCheckout: {
            ...checkout.checkout.value!.staffCheckout,
            id: staffCheckoutId,
            mode: intent.mode,
            payments: legs,
            intent: isLastLeg ? intent : undefined,
          },
          ...(isLastLeg
            ? {}
            : {
                discount: undefined,
                promotions: keptPromotions,
                customer: checkout.checkout.value?.customer
                  ? {
                      ...checkout.checkout.value.customer,
                      reward_selections: [],
                    }
                  : undefined,
                giftCards: [],
                // Clear the tip we set for the summary above so it doesn't
                // carry into the next leg.
                tip: undefined,
              }),
        });

        if (isLastLeg) {
          // Last leg — materialize the order and show the final success.
          // Its own boundary: the leg above IS collected and already recorded in
          // checkout state, so a finalize failure must not be reported as
          // "failed to collect payment" (it would read as if the money hadn't
          // been taken). The checkout stays fully collected and `awaitingFinalize`
          // offers the retry.
          try {
            // The order is finalized SERVER-SIDE, inline with this last leg
            // (add_split_payment / add_partial_payment call staff_checkouts_finalize
            // in the same transaction). The built order comes back as `res.result`,
            // so we render it directly WITHOUT a separate finalize round-trip.
            // Fallback to the idempotent finalize read-back only if the inline
            // result is absent (inline finalize errored, or older backend).
            if (res.result) {
              await renderStaffCheckoutSuccess(res.result);
            } else {
              await maybeFinalizeStaffCheckout();
            }
          } catch (e: any) {
            console.error("finalize after last leg failed", e);
            Sentry.captureException(e);
            finalizeError.value = e?.message || null;
            toast.showToast(
              e?.message ||
                t("checkout.staged.finalize_failed") ||
                "Payment collected, but the order could not be created. Tap Finish order to retry.",
              { type: "error" },
            );
          }
        } else {
          // Intermediate leg — per-leg confirmation, checkout stays open.
          handleLegCollected({
            type: "staff_checkout_payment_collected",
            staffCheckoutId,
            paymentId: res.payment_id,
            paymentGuid: res.payment_guid,
            amount: legNet,
            // Tip for this leg, so the per-leg success screen shows amount + tip.
            tip: tipAmount,
            subtotal: intent.amount,
            method,
            items: legItems,
          });
        }
      } catch (e: any) {
        console.error("collectStagedPayment failed", e);
        toast.showToast(e?.message || "Failed to collect payment", {
          type: "error",
        });
      } finally {
        setCheckoutLoadingState(false);
      }
    };

    const stopPaymentIntegrationPolling = () => {
      if (paymentIntegrationPollingInterval.value) {
        clearInterval(paymentIntegrationPollingInterval.value);
        paymentIntegrationPollingInterval.value = null;
      }
    };

    const pollPaymentIntegrationStatus = async () => {
      // Skip if already polling (prevents multiple concurrent requests)
      if (paymentIntegrationPollingInProgress.value) {
        return;
      }

      const checkoutData = checkout.checkout.value;
      const workflowId = checkoutData?.paymentIntegrationWorkflowId;
      const paymentId = checkoutData?.paymentId;

      if (!usePaymentIntegrationV2.value || !workflowId || !paymentId) {
        stopPaymentIntegrationPolling();
        return;
      }

      // A poll that never settles would pin `pollingInProgress` forever and
      // every later tick would no-op: polling silently dead for the rest of the
      // payment, with no recovery path. Bound each poll so the loop always gets
      // its next tick.
      const controller = new AbortController();
      const pollTimeout = setTimeout(
        () => controller.abort("poll-timeout"),
        POLL_REQUEST_TIMEOUT_MS,
      );

      try {
        paymentIntegrationPollingInProgress.value = true;

        instrumentation.track("checkout_integrated_payment_poll_attempted", {
          checkout_id: props.checkoutId,
          payment_id: paymentId,
          payment_workflow_id: workflowId,
        });
        // Trace each poll as a span rejoining the payment's trace. See
        // paymentTrace.ts: this is a segment span, not a child of the payment
        // root, so it is sent independently of any other span's lifetime.
        //
        // Everything needed to diagnose a poll is recorded ON THIS SPAN --
        // outcome, workflow status, the server's own view via `poll_index`.
        // The auto-instrumented `http.client` child is NOT reliable (the
        // browser SDK mis-parents or drops it), so nothing here depends on it.
        const pollIndex = paymentIntegrationPollIndex.value++;
        const workflowStatus = await startPaymentSpan(
          props.checkoutId,
          {
            name: "checkout.integrated_payment.poll",
            op: "checkout.payment",
            attributes: {
              checkout_id: props.checkoutId,
              payment_id: Number(paymentId),
              workflow_id: workflowId,
              poll_index: pollIndex,
              poll_interval_ms: paymentIntegrationPollIntervalMs.value,
            },
          },
          async (span) => {
            try {
              const result =
                await trpc.integratedPaymentWorkflow.getStatus.query(
                  { paymentId: Number(paymentId), pollIndex },
                  { signal: controller.signal },
                );
              span.setAttribute("poll_outcome", "ok");
              span.setAttribute(
                "workflow_status",
                result.output?.status ?? "unknown",
              );
              return result;
            } catch (error: any) {
              // Record the outcome before rethrowing, so a timed-out or failed
              // poll is distinguishable from a slow successful one on the span
              // alone rather than only in a separate analytics event.
              const timedOut = isAbortError(error);
              span.setAttribute("poll_outcome", timedOut ? "timeout" : "error");
              if (!timedOut) {
                span.setAttribute("error_message", error?.message ?? "unknown");
              }
              span.setStatus({
                code: 2,
                message: timedOut ? "deadline_exceeded" : "internal_error",
              });
              throw error;
            }
          },
        );
        //
        instrumentation.track("checkout_integrated_payment_poll_success", {
          checkout_id: props.checkoutId,
          payment_id: paymentId,
          payment_workflow_id: workflowId,
          payment_workflow_status: workflowStatus.output?.status,
        });

        // Check if checkout still exists (might have been cancelled)
        if (!checkout.checkout.value) {
          stopPaymentIntegrationPolling();
          return;
        }

        // Update checkout with workflow status (only if status changed to avoid triggering watch)
        const currentStatus =
          checkout.checkout.value.paymentIntegrationWorkflowStatus?.status;
        const newStatus = workflowStatus.output?.status as
          | IntegratedPaymentWorkflowStatus
          | undefined;

        if (workflowStatus.output && newStatus && currentStatus !== newStatus) {
          checkout.setCheckout({
            ...checkout.checkout.value,
            paymentIntegrationWorkflowStatus: {
              status: newStatus,
              error: workflowStatus.output.error,
              payment: workflowStatus.payment,
            },
          });
        }

        // Check if workflow is still running
        if (
          !workflowStatus.output ||
          workflowStatus.output.status === "initializing" ||
          workflowStatus.output.status === "waiting_for_provider" ||
          workflowStatus.output.status === "provider_attached" ||
          workflowStatus.output.status === "waiting_for_confirmation" ||
          workflowStatus.output.status === "payment_confirmed" ||
          workflowStatus.output.status === "processing_payment"
        ) {
          // Keep polling
          return;
        }

        // Workflow has completed or failed
        stopPaymentIntegrationPolling();

        // Check again if checkout still exists before updating
        if (!checkout.checkout.value) {
          return;
        }

        if (workflowStatus.output.status === "failed") {
          instrumentation.track("checkout_integrated_payment_failed", {
            checkout_id: props.checkoutId,
            payment_id: paymentId,
            payment_workflow_id: workflowId,
            payment_workflow_error: workflowStatus.output.error,
          });
          checkout.setCheckout({
            ...checkout.checkout.value,
            txLoading: false,
            paymentIntegrationWorkflowStatus: {
              status: "failed",
              error: workflowStatus.output.error,
              payment: workflowStatus.payment,
            },
          });
          // The payment modal already explains a coded error; no toast on top.
          if (!isPaymentErrorCode(workflowStatus.output.error)) {
            toast.showToast(
              workflowStatus.output.error ||
                t("checkout.integrated_payment.payment_failed"),
              { type: "error" },
            );
          }
          // Terminal state: close out the payment's trace.
          endPaymentTrace(props.checkoutId, "failed");
          return;
        }

        if (workflowStatus.output.status === "completed") {
          instrumentation.track("checkout_integrated_payment_completed", {
            checkout_id: props.checkoutId,
            payment_id: paymentId,
            payment_workflow_id: workflowId,
          });
          endPaymentTrace(props.checkoutId, "ok");
          const result = workflowStatus.output.checkoutResult;
          // A staged split/partial terminal/online leg — collected, but the
          // order is NOT created yet. Record the leg and stay in the checkout;
          // finalize once the balance reaches zero (same trigger as cash/manual).
          if (result?.type === "staff_checkout_payment_collected") {
            const cd = checkout.checkout.value;
            if (cd?.staffCheckout?.id) {
              const legs: StaffCheckoutStagedPayment[] = [
                ...(cd.staffCheckout.payments || []),
                {
                  paymentId: result.paymentId,
                  paymentGuid: result.paymentGuid ?? "",
                  amount: result.amount,
                  subtotal: result.subtotal ?? result.amount,
                  method: result.method,
                  items: result.items,
                },
              ];
              const target = calculateCheckoutSubTotal(cd);
              const collectedNow = legs.reduce((s, p) => s + p.subtotal, 0);
              const isLastLeg = round(target - collectedNow) <= 0.01;
              checkout.setCheckout({
                ...cd,
                staffCheckout: {
                  ...cd.staffCheckout,
                  mode: result.items ? "partial" : "split",
                  payments: legs,
                  // Keep the armed intent on the final leg so the summary stays
                  // on the leg amount through the finalize round-trip instead of
                  // flashing the whole-cart TOTAL (see the cash/manual path).
                  intent: isLastLeg ? cd.staffCheckout.intent : undefined,
                },
                txLoading: false,
                paymentId: undefined,
                paymentIntegrationWorkflowId: undefined,
                paymentIntegrationWorkflowPlatform: undefined,
                paymentIntegrationWorkflowStatus: undefined,
                // Adjustments ride the leg that collected them: clear the
                // checkout-level discount / promotions / gift cards / tip on an
                // INTERMEDIATE leg so the next leg starts clean (mirrors the
                // cash/manual reset in collectStagedPayment). On the FINAL leg
                // keep them so the summary doesn't flash before finalize.
                ...(isLastLeg
                  ? {}
                  : {
                      discount: undefined,
                      promotions: [],
                      giftCards: [],
                      tip: undefined,
                    }),
              });
              if (isLastLeg) {
                // Own boundary: the card leg IS captured and recorded above, so
                // a finalize failure must not surface as a polling error (the
                // outer catch's message) and must not look like a failed
                // payment. `awaitingFinalize` offers the retry.
                //
                // NOTE: for the integrated path the order is already created
                // server-side by the apply_transition hook when the leg reached
                // 'confirmed'. This `finalize` call is therefore an IDEMPOTENT
                // READ-BACK (returns the existing order under a row lock, never a
                // second order) — the poll result only carries the leg, not the
                // finalized cart order, so we fetch it here. (Eliminating even
                // this call would require getStatus to return the finalized cart
                // result once the checkout is completed.)
                try {
                  await maybeFinalizeStaffCheckout();
                } catch (e: any) {
                  console.error("finalize after last integrated leg failed", e);
                  Sentry.captureException(e);
                  finalizeError.value = e?.message || null;
                  toast.showToast(
                    e?.message ||
                      t("checkout.staged.finalize_failed") ||
                      "Payment collected, but the order could not be created. Tap Finish order to retry.",
                    { type: "error" },
                  );
                }
              } else {
                handleLegCollected(result);
              }
            }
            return;
          }
          printCartCheckoutResult(result);
          handleTxSuccess(result);
        } else {
          // Any other terminal status (aborted, timeout, ...) still ends the
          // payment; the loop has already stopped above.
          endPaymentTrace(props.checkoutId, "abandoned");
        }
      } catch (error: any) {
        if (isAbortError(error)) {
          // Our own timeout fired. The next tick re-polls, so this is expected
          // backpressure rather than a failure worth reporting.
          instrumentation.track("checkout_integrated_payment_poll_timeout", {
            checkout_id: props.checkoutId,
            payment_id: paymentId,
            payment_workflow_id: workflowId,
            timeout_ms: POLL_REQUEST_TIMEOUT_MS,
          });
          return;
        }
        console.error("Error polling payment integration workflow:", error);
        instrumentation.track("checkout_integrated_payment_poll_error", {
          checkout_id: props.checkoutId,
          payment_id: paymentId,
          payment_workflow_id: workflowId,
          error_message: error?.message,
        });
        // Don't stop polling on error - continue trying
      } finally {
        clearTimeout(pollTimeout);
        paymentIntegrationPollingInProgress.value = false;
      }
    };

    watch(
      () => [
        usePaymentIntegrationV2.value,
        checkout.checkout.value?.paymentIntegrationWorkflowId,
        checkout.checkout.value?.txLoading,
        checkout.checkout.value?.txSuccess,
      ],
      () => {
        const checkoutData = checkout.checkout.value;
        const shouldPoll =
          usePaymentIntegrationV2.value &&
          !!checkoutData?.paymentIntegrationWorkflowId &&
          !!checkoutData?.txLoading &&
          !checkoutData?.txSuccess;

        if (!shouldPoll) {
          stopPaymentIntegrationPolling();
          return;
        }

        // Only start polling if not already polling (prevents restarting interval unnecessarily)
        if (!paymentIntegrationPollingInterval.value) {
          // Restart the sequence so poll_index is per-payment, not per-mount.
          paymentIntegrationPollIndex.value = 0;
          pollPaymentIntegrationStatus();
          // getStatus is a pure DB read (no cross-service RPC), so polling is
          // cheap; the interval tracks how fast the workflow platform actually
          // drives the payment row rather than a fixed guess.
          paymentIntegrationPollingInterval.value = setInterval(
            pollPaymentIntegrationStatus,
            paymentIntegrationPollIntervalMs.value,
          );
        }
      },
      { immediate: true },
    );

    return () => {
      const checkoutData = checkout.checkout.value;
      if (!checkoutData) return null;
      //      return (
      //        <NewCheckoutComponent
      //          checkoutData={checkout.checkout.value}
      //          onCancel={() => {
      //            //
      //            props.onCancel();
      //          }}
      //          onCheckoutUpdate={() => {
      //            //
      //          }}
      //          onProcessCheckout={() => {
      //            //
      //          }}
      //        />
      //      );
      console.log("checkoutId", props.checkoutId);
      return (
        <FullScreenSheet
          isOpen={!!checkoutData}
          // Runs BEFORE the sheet starts closing, so a veto genuinely keeps the
          // cashier on the checkout. Doing this in onClose (as this used to)
          // could not work: by then the sheet has already unmounted itself and
          // cannot be brought back, so the cashier got the warning on an empty
          // screen with the checkout still uncancelled.
          canClose={async () => {
            if (isPartPaid.value && !(await confirmLeavePartPaid())) {
              return false;
            }
            if (txSuccess.value) return true;
            // Leaving the screen never discards money: a checkout with collected
            // legs is RETAINED open (see cancelStaffCheckout / staff_checkouts_cancel)
            // and stays fully recoverable from Transaction History. So we never
            // block the close — an empty checkout is cancelled, a collected one is
            // saved, and we just let the cashier know where to find it.
            const { retained } = await cancelStaffCheckout();
            if (retained) {
              toast.showToast(
                t("checkout.messages.collected_saved_to_history") ||
                  "This checkout has a collected payment. It's been saved to Transaction History, where you can resume, finish, or cancel it.",
                { type: "info" },
              );
            }
            return true;
          }}
          // Reached only once canClose allowed it (or the parent closed us).
          onClose={() => {
            if (txSuccess.value) {
              props.onSuccess();
              return;
            }
            props.onCancel();
          }}
        >
          {{
            default: ({ close }: FullScreenSheetSlotProps) => (
              <div
                style={{
                  paddingTop: `var(--safe-area-top, 0px)`,
                }}
                class="flex flex-col h-full w-full glossy-bg overflow-hidden"
              >
                <div class="flex justify-between items-center px-2 lg:px-6 py-2">
                  <div class="flex items-center gap-3">
                    {/* Hidden on the per-leg success screen ("Continue" is the
                        only way forward). While the split is in progress (a
                        leg collected, order not yet finalized) canClose asks
                        the cashier to confirm before leaving. */}
                    <a
                      href="#"
                      style={{
                        visibility: isLegSuccess.value ? "hidden" : "visible",
                      }}
                      onClick={async (e) => {
                        e.preventDefault();
                        close();
                      }}
                      class="text-accent text-2xl"
                    >
                      <span class="flex justify-start items-center">
                        <span class="material-symbols-outlined text-4xl mr-1">
                          arrow_back
                        </span>
                        {t("pos.pay.back_button")}
                      </span>
                    </a>
                  </div>
                  <div class="flex gap-2">
                    <button
                      id="print-rcpt-button"
                      disabled={
                        checkout.checkout.value?.payload?.type !== "table" ||
                        isPrinting.value
                      }
                      class="btn btn-ghost btn-circle text-accent"
                      onClick={async () => {
                        try {
                          isPrinting.value = true;
                          const checkoutData = checkout.checkout.value;
                          if (checkoutData?.payload?.type !== "table") return;
                          const subtotal = calculateCheckoutTotal(checkoutData);
                          const tables = await api.getTablesMeta();
                          const tableId = checkoutData.payload.object.table_id;
                          const table = tables
                            .map((el) => el.table)
                            .find((el) => el.id === tableId);
                          if (!table) throw new Error("table not found");
                          const printJob = await getPrintJobForTableBill(
                            tableId,
                            checkoutData.payload.object.type === "partial"
                              ? checkoutData.payload.object.items
                              : undefined,
                            subtotal,
                            table,
                            checkoutData.discount?.type
                              ? {
                                  type: checkoutData.discount.type,
                                  amount: checkoutData.discount.value,
                                  category: checkoutData.discount.category,
                                  net: checkoutData.discount.amount,
                                }
                              : undefined,
                            checkoutData.payload.object.type === "split"
                              ? {}
                              : undefined,
                          );
                          await shop.printReceipt(printJob);
                          toast.showToast("Receipt printed successfully", {
                            type: "success",
                            icon: "print",
                            autoHide: 3000,
                          });
                        } catch (e) {
                          console.error("Failed to print bill:", e);
                          try {
                            Sentry.captureException(e);
                            console.error(e);
                          } catch {}
                        } finally {
                          isPrinting.value = false;
                        }
                      }}
                    >
                      <span class="material-symbols-outlined text-2xl">
                        print
                      </span>
                    </button>
                    <button
                      id="show-qr-scanner-button"
                      aria-label={t("loyalty.rewards.customer_rewards")}
                      disabled={!!txSuccess.value || !!loading.value}
                      class="btn btn-ghost btn-circle text-accent"
                      onClick={() => (showLoyaltyModal.value = true)}
                    >
                      <span class="material-symbols-outlined text-2xl">
                        qr_code_scanner
                      </span>
                    </button>
                    {showSplitButton.value ? (
                      // Cart checkout: split/partial sheet is the only editor.
                      // Rendered throughout (disabled while the staff_checkout
                      // is being created or a leg is collecting) so it doesn't
                      // flicker with the generic edit button.
                      <button
                        class="btn btn-primary btn-circle"
                        disabled={!canSplitPartial.value}
                        onClick={openSplitEditor}
                      >
                        <span class="material-symbols-outlined text-xl">
                          call_split
                        </span>
                      </button>
                    ) : import.meta.env.VITE_ENV === "staging" &&
                      checkout.checkout.value?.payload?.type === "table" &&
                      !txSuccess.value &&
                      !loading.value ? (
                      // Table checkout: open the table split/partial editor.
                      <button
                        class="btn btn-ghost btn-circle text-accent"
                        title="Edit order"
                        onClick={() => (showCheckoutEditor.value = true)}
                      >
                        <span class="material-symbols-outlined text-2xl">
                          edit_note
                        </span>
                      </button>
                    ) : null}
                  </div>
                </div>
                <div class="flex flex-1 overflow-hidden">
                  <div class="flex-1 flex flex-col justify-start py-12 items-center overflow-y-auto">
                    <div class="pb-4 text-center">
                      {txSuccess.value ? (
                        <TransactionSuccess
                          onCancel={props.onCancel}
                          onDigitalReceiptOpen={clearCloseTimeout}
                          onSuccess={props.onSuccess}
                          result={txSuccess.value}
                          // Close shows on every terminal completion (normal
                          // checkout and the finalized last leg). Intermediate
                          // legs use a different result branch with "Continue".
                          hideClose={false}
                          onCollectNext={
                            isLegSuccess.value ? collectNextLeg : undefined
                          }
                          onPrintLeg={printLeg}
                          // Per-leg screen: show gross coverage collected so far
                          // (incl. this leg) against the checkout total.
                          collectedTotal={
                            isLegSuccess.value
                              ? (
                                  checkout.checkout.value?.staffCheckout
                                    ?.payments ?? []
                                ).reduce((s, p) => s + (p.subtotal ?? 0), 0)
                              : undefined
                          }
                          targetTotal={
                            isLegSuccess.value && checkout.checkout.value
                              ? calculateCheckoutSubTotal(
                                  checkout.checkout.value,
                                )
                              : undefined
                          }
                        />
                      ) : (
                        <TransactionSummary />
                      )}
                      {loading.value ? (
                        <div class="flex flex-col gap-4 py-8">
                          <div class="flex justify-center items-center">
                            <Loading />
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div class="px-4 lg:px-0 lg:py-12 w-full lg:w-auto">
                      {txSuccess.value ||
                      loading.value ? null : awaitingFinalize.value ? (
                        // Fully collected, order not created.
                        //
                        // This exists to stop a cashier re-charging someone who
                        // has already paid, so it leads with "paid in full" and
                        // the amount taken. Kept to two lines: at a till nobody
                        // reads a paragraph. The pay buttons are deliberately
                        // gone — there is nothing left to collect and offering
                        // them would invite exactly that mistake.
                        <div class="mx-auto flex w-full max-w-sm flex-col gap-3 rounded-2xl border border-warning/40 bg-warning/5 p-4 text-left">
                          <div class="flex items-start gap-2.5">
                            <span class="material-symbols-outlined shrink-0 text-xl leading-none text-warning">
                              price_check
                            </span>
                            <div class="min-w-0 flex-1">
                              <p class="text-sm font-semibold leading-snug text-base-content">
                                {t("checkout.staged.awaiting_finalize_title") ||
                                  "Paid in full — order not created yet"}
                              </p>
                              <p class="mt-0.5 text-xs text-base-content/60">
                                {money(collectedTotal.value)}{" "}
                                {t("checkout.staged.collected_total") ||
                                  "collected"}
                                {" · "}
                                {collectedLegs.value.length}{" "}
                                {t("reports.tx.open_checkouts.payments") ||
                                  "payments"}
                              </p>
                            </div>
                          </div>

                          {/* The actual reason, when we have one: "outside shift
                              hours" means fetch a manager, where a dropped
                              connection just needs another tap. */}
                          {finalizeError.value && (
                            <p class="break-words rounded-lg bg-base-100/70 px-2.5 py-1.5 text-xs leading-snug text-base-content/60">
                              {finalizeError.value}
                            </p>
                          )}

                          <button
                            class="btn btn-primary btn-block gap-2"
                            onClick={finalizeNow}
                          >
                            <span class="material-symbols-outlined text-xl">
                              receipt_long
                            </span>
                            {t("checkout.staged.finish_order") ||
                              "Finish order"}
                          </button>
                        </div>
                      ) : usePaymentIntegrationV2.value ? (
                        <CheckoutActionsV2
                          checkoutId={props.checkoutId}
                          onPayFromCredit={handlePayFromCredit}
                          showCardModal={showCardModal}
                          showCashModal={showCashModal}
                          showDiscountModal={showDiscountModal}
                          showProductDiscountEditorSheet={
                            showProductDiscountEditorSheet
                          }
                          showTerminalModal={showTerminalModal}
                          showManualModal={showManualModal}
                          showOnlineModal={showOnlineModal}
                          showPromotionsModal={showPromotionsModal}
                          showGiftCardModal={showLoyaltyModal}
                        />
                      ) : (
                        <CheckoutActions
                          showCardModal={showCardModal}
                          showCashModal={showCashModal}
                          showDiscountModal={showDiscountModal}
                          showProductDiscountEditorSheet={
                            showProductDiscountEditorSheet
                          }
                          showTerminalModal={showTerminalModal}
                          showManualModal={showManualModal}
                          showOnlineModal={showOnlineModal}
                          showPromotionsModal={showPromotionsModal}
                          showGiftCardModal={showLoyaltyModal}
                        />
                      )}
                    </div>
                  </div>
                </div>
                {showDiscountModal.value ? (
                  <DiscountModal
                    checkout={checkoutData}
                    onClose={() => (showDiscountModal.value = false)}
                    onConfirm={handleDiscountConfirm}
                    total={adjustmentBase.value}
                  />
                ) : null}
                {showProductDiscountEditorSheet.value &&
                checkout.checkout.value &&
                canUseProductDiscountEditor.value ? (
                  <ProductDiscountEditorSheet
                    isOpen={showProductDiscountEditorSheet.value}
                    checkoutData={checkout.checkout.value}
                    onClose={() =>
                      (showProductDiscountEditorSheet.value = false)
                    }
                    onApply={handleProductDiscountApply}
                  />
                ) : null}
                {leaveConfirmResolve.value ? (
                  <LeavePartPaidConfirmModal
                    collected={money(collectedTotal.value)}
                    onConfirm={() => answerLeaveConfirm(true)}
                    onCancel={() => answerLeaveConfirm(false)}
                  />
                ) : null}
                {showCashModal.value ? (
                  <CashModal
                    isOpen={showCashModal.value}
                    onClose={() => (showCashModal.value = false)}
                    onConfirm={handleCashConfirm}
                    totalAmount={total.value}
                  />
                ) : null}
                {showCardModal.value ? (
                  <CardModal
                    isOpen={showCardModal.value}
                    onClose={() => (showCardModal.value = false)}
                    onConfirm={handleCardConfirm}
                    totalAmount={total.value}
                  />
                ) : null}
                {/* Workflow Payment Status Modal - shown automatically when workflow is running */}
                {usePaymentIntegrationV2.value &&
                checkout.checkout.value?.paymentIntegrationWorkflowId &&
                !checkout.checkout.value?.txSuccess ? (
                  <WorkflowPaymentStatusModal
                    workflowId={
                      checkout.checkout.value.paymentIntegrationWorkflowId
                    }
                    workflowStatus={
                      checkout.checkout.value.paymentIntegrationWorkflowStatus
                    }
                    onClose={() => {
                      // Handle close - stop polling, clear workflow
                      stopPaymentIntegrationPolling();
                      if (checkout.checkout.value) {
                        endPaymentTrace(props.checkoutId, "abandoned");
                        checkout.setCheckout({
                          ...checkout.checkout.value,
                          paymentIntegrationWorkflowId: undefined,
                          paymentIntegrationWorkflowPlatform: undefined,
                          paymentIntegrationWorkflowStatus: undefined,
                          txLoading: false,
                        });
                      }
                    }}
                    onCancel={() => {
                      // Closed after a cancel: stop polling and close the
                      // payment's root span, but keep the checkout open so the
                      // cashier can retry or pick another payment method.
                      stopPaymentIntegrationPolling();
                      endPaymentTrace(props.checkoutId, "cancelled");
                      if (checkout.checkout.value) {
                        checkout.setCheckout({
                          ...checkout.checkout.value,
                          paymentIntegrationWorkflowId: undefined,
                          paymentIntegrationWorkflowPlatform: undefined,
                          paymentIntegrationWorkflowStatus: undefined,
                          txLoading: false,
                        });
                      }
                    }}
                  />
                ) : null}
                {/* Legacy terminal modals - shown when NOT using payment integration v2 */}
                {!usePaymentIntegrationV2.value && showTerminalModal.value ? (
                  <TerminalPaymentModal
                    onClose={() => (showTerminalModal.value = false)}
                    onConfirm={handleTerminalConfirm}
                    totalAmount={total.value}
                    paymentMethod={showTerminalModal.value}
                    checkoutId={props.checkoutId}
                  />
                ) : null}
                {showOnlineModal.value ? (
                  <OnlinePaymentModalV3
                    onClose={() => (showOnlineModal.value = false)}
                    onConfirm={handleOnlineConfirm}
                    totalAmount={total.value}
                    checkoutId={props.checkoutId}
                  />
                ) : null}
                {showManualModal.value ? (
                  <ManualPaymentModal
                    onClose={() => (showManualModal.value = false)}
                    onConfirm={handleManualConfirm}
                    totalAmount={total.value}
                  />
                ) : null}
                {showPromotionsModal.value ? (
                  <PromotionsModal
                    onClose={() => (showPromotionsModal.value = false)}
                  />
                ) : null}
                {checkout.checkout.value?.customer && !txSuccess.value && (
                  <button
                    type="button"
                    class="btn btn-ghost h-auto min-h-12 w-full justify-between whitespace-normal border border-base-300 p-3 text-left"
                    disabled={loading.value}
                    onClick={() => (showLoyaltyModal.value = true)}
                  >
                    <span class="min-w-0 break-words">
                      {checkout.checkout.value.customer.display_name ||
                        t("loyalty.modal.customer_identified")}
                    </span>
                    <span class="text-primary">
                      {t("loyalty.rewards.edit_rewards")}
                    </span>
                  </button>
                )}
                {showLoyaltyModal.value ? (
                  <LoyaltyModal
                    isOpen={showLoyaltyModal.value}
                    onClose={() => (showLoyaltyModal.value = false)}
                    totalAmount={total.value}
                    onApply={handleGiftCardApply}
                    onCustomerResolved={handleCustomerResolved}
                    onCustomerRemoved={() => {
                      const cd = checkout.checkout.value;
                      if (!cd) return;
                      const ids = new Set(
                        (shop.getPromotions() as Promotion[])
                          .filter(isLoyaltyPromotion)
                          .map((p) => p.id),
                      );
                      checkout.setCheckout({
                        ...cd,
                        customer: undefined,
                        promotions: (cd.promotions ?? []).filter(
                          (p) => !ids.has(p.promotion_id),
                        ),
                      });
                    }}
                    remainingTotal={total.value}
                  />
                ) : null}
                {showCheckoutEditor.value && checkout.checkout.value ? (
                  <CheckoutEditor
                    isOpen={showCheckoutEditor.value}
                    checkoutData={checkout.checkout.value}
                    onClose={() => (showCheckoutEditor.value = false)}
                    onUpdate={(updated) => {
                      checkout.setCheckout(updated);
                    }}
                  />
                ) : null}
              </div>
            ),
          }}
        </FullScreenSheet>
      );
    };
  },
});
